import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

import { AllCompaniesGQL, Company } from '../../graphql/graphql';
import { CompaniesListDatasource } from './companies-list.datasource';
import { FilterInterface } from '../../shared/filter-bar/filter-bar.component';
import { ManagerAssignComponent } from '../manager-assign/manager-assign.component';
import {
  OffboardingRequirementsDialogComponent
} from '../offboarding/offboarding-requirements-dialog/offboarding-requirements-dialog.component';
import { OffboardingService } from '../offboarding/offboarding.service';

@Component({
  selector: 'app-companies-list',
  templateUrl: './companies-list.component.html',
  styleUrls: ['./companies-list.component.scss']
})
export class CompaniesListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTable) table: MatTable<Company>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  dataSource: CompaniesListDatasource;
  displayedColumns = [
    'company', 'manager', 'substituteManager', 'recruiter', 'substituteRecruiter', 'color', 'last-color-change', 'checkbox'
  ];
  selectionList: Company[] = [];
  offboardingRolesSet = {};

  filters;

  constructor(
    private companiesGQL: AllCompaniesGQL,
    private dialog: MatDialog,
    private offboardingService: OffboardingService
  ) { }

  ngOnInit() {
    this.getOffboardingRoles();
    this.dataSource = new CompaniesListDatasource(this.companiesGQL, this.selectionList);
  }

  getOffboardingRoles() {
    this.offboardingService.getAllRoles()
    .subscribe(v => {
        v.data.offboardingRoles.edges.map(value => {
          if (this.offboardingRolesSet[value.node.company.id]?.length) {
            this.offboardingRolesSet[value.node.company.id].push({
              id: value.node.id,
              companyId: value.node.company.id,
              name: value.node.name,
              updatedAt: value.node.updatedAt,
              createdAt: value.node.createdAt,
              actions: value.node.actions.map(item =>
                value.node.actions.length ? item.description : []
              ),
            });
          } else {
            this.offboardingRolesSet[value.node.company.id] = [{
              id: value.node.id,
              companyId: value.node.company.id,
              name: value.node.name,
              updatedAt: value.node.updatedAt,
              createdAt: value.node.createdAt,
              actions: value.node.actions.map(item =>
                value.node.actions.length ? item.description : []
              ),
            }];
          }
        });
      }
    );
  }

  getPreparedOffboardingRoles() {
     const raw = this.selectionList.map(v => this.offboardingRolesSet[v.id]).filter(v => !!v);
     if (raw.length) {
       let result = [];
       raw.map(v => {
         result = result.concat(v);
       });
       return result;
     } else {
       return [];
     }
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  onFilterChanges(event: FilterInterface) {
    const filters = {};
    if (event.search) { filters['name'] = event.search; }
    if (event.country) { filters['country'] = event.country; }
    if (event.projectManager) { filters['projectManager'] = event.projectManager; }
    if (event.substituteManager) { filters['substituteManager'] = event.substituteManager; }
    if (event.recruiter) { filters['recruiter'] = event.recruiter; }
    if (event.substituteRecruiter) { filters['substituteRecruiter'] = event.substituteRecruiter; }
    this.filters = filters;
    this.dataSource.filters.next(filters);
  }

  selectRow(company: Company) {
    if (!!this.selectionList.find(item => item.id === company.id)) {
      const idx = this.selectionList.findIndex(item => item.id === company.id);
      this.selectionList.splice(idx, 1);
    } else {
      this.selectionList.push(company);
    }
  }

  isSelected(company: Company): boolean {
    return !!this.selectionList.find(item => item.id === company.id);
  }

  openManagerAssign() {
    this.dialog.open(ManagerAssignComponent, {
      data: {
        companies: this.selectionList,
        preselect: this.selectionList.length === 1 ? this.selectionList[0] : undefined
      },
      autoFocus: false
    }).afterClosed().pipe(take(1)).subscribe(res => {
      if (res) {
        this.selectionList.splice(0, this.selectionList.length);
        this.dataSource.filters.next(this.filters);
      }
    });
  }

  openOffboardingRequirements() {
    this.dialog.open(OffboardingRequirementsDialogComponent, {
      data: {
        roles: this.getPreparedOffboardingRoles(),
        companies: this.selectionList.map(v => v.id)
      },
      width: '750px',
      panelClass: 'offboarding-requirements',
    }).afterClosed().subscribe(res => {
      if (res) {
        this.manageOffboardingRequests(res);
      }
    });
  }

  manageOffboardingRequests(res) {
    res.data.map(value => {
      const input = {
        companyId: '',
        name: value.name,
        actions: value.actions
      };
      if (value?.needCreate) {
        value.companyId.map(id => {
          input.companyId = id;
          this.offboardingService.createRole(input).subscribe(role => {
            const newRole = {
              id: role.data.createOffboardingRole.offboardingRole.id,
              companyId: role.data.createOffboardingRole.offboardingRole.company.id,
              name: role.data.createOffboardingRole.offboardingRole.name,
              updatedAt: role.data.createOffboardingRole.offboardingRole.updatedAt,
              createdAt: role.data.createOffboardingRole.offboardingRole.createdAt,
              actions: role.data.createOffboardingRole.offboardingRole.actions.map(item =>
                role.data.createOffboardingRole.offboardingRole.actions.length ? item.description : []
              ),
            };
            if (this.offboardingRolesSet[id]) {
              this.offboardingRolesSet[id].push(newRole);
            } else {
              this.offboardingRolesSet[id] = [newRole];
            }
          });
        });
      } else if (value?.needUpdate) {
        input.companyId = value.companyId;
        this.offboardingService.updateRole(value.id, input).subscribe(result => {
          this.offboardingRolesSet[value.companyId] = this.offboardingRolesSet[value.companyId].map(role => {
            if (role.id === result.data.updateOffboardingRole.offboardingRole.id) {
              return {
                id: result.data.updateOffboardingRole.offboardingRole.id,
                companyId: result.data.updateOffboardingRole.offboardingRole.company.id,
                name: result.data.updateOffboardingRole.offboardingRole.name,
                updatedAt: result.data.updateOffboardingRole.offboardingRole.updatedAt,
                createdAt: result.data.updateOffboardingRole.offboardingRole.createdAt,
                actions: result.data.updateOffboardingRole.offboardingRole.actions.map(item =>
                  result.data.updateOffboardingRole.offboardingRole.actions.length ? item.description : []
                )
              };
            } else {
              return role;
            }
          });
        });
      }
    });

    if (res.needsToBeDeleted && res.needsToBeDeleted.length) {
      res.needsToBeDeleted.map((item) => {
        this.offboardingService.deleteRole(item.id).subscribe(role => {
          if (role.data.deleteOffboardingRole.ok) {
            this.offboardingRolesSet[item.companyId] =
              this.offboardingRolesSet[item.companyId].filter(v => v.id !== item.id);
          }
        });
      });
    }
  }
}
